/**
 * Template "Checklist de Inspeção de Motocicleta" (Loca9Motos).
 * IDs alinhados com a API de template (itens 1–71).
 */
export const CHECKLIST_TEMPLATE_IDS = {
  plate: 1,
  model: 2,
  odometer: 3,
  checklistDate: 4,
  checklistTime: 5,
  driverName: 6,
  driverCpf: 7,
  driverPhone: 8,
  panelKm: 9,
  tireCondition: 10,
  fuelLevel: 11,
  isDirty: 12,
  photoFront: 13,
  photoRight: 14,
  photoLeft: 15,
  photoTop: 16,
  photoBack: 17,
  detailPhoto: 18,
  observations: 65,
  signatureName: 66,
  signaturePhone: 67,
  signatureEmail: 68,
  signatureCpf: 69,
  signaturePhoto: 70,
  documentPdf: 71
} as const;

/** Indica item de anexo PDF do checklist (template 71 ou URL de PDF). */
export function isChecklistPdfAttachment(
  templateId: number | null | undefined,
  photoUrl?: string | null
): boolean {
  if (templateId === CHECKLIST_TEMPLATE_IDS.documentPdf) {
    return true;
  }
  if (!photoUrl?.trim()) {
    return false;
  }
  const url = photoUrl.toLowerCase();
  return (
    url.includes('application/pdf') ||
    url.endsWith('.pdf') ||
    url.includes('.pdf?') ||
    url.includes('/checklistpdf/') ||
    url.includes('checklistpdf')
  );
}

export const CHECKLIST_PHOTO_SIDE_TO_TEMPLATE: Record<string, number> = {
  frente: CHECKLIST_TEMPLATE_IDS.photoFront,
  direita: CHECKLIST_TEMPLATE_IDS.photoRight,
  esquerda: CHECKLIST_TEMPLATE_IDS.photoLeft,
  cima: CHECKLIST_TEMPLATE_IDS.photoTop,
  traseira: CHECKLIST_TEMPLATE_IDS.photoBack
};

export interface ChecklistAccessoryField {
  /** Chave do FormControl: acc_{templateId} */
  key: string;
  templateId: number;
  label: string;
}

export interface ChecklistAccessoryGroup {
  title: string;
  items: ChecklistAccessoryField[];
}

/** Acessórios (seção 6) agrupados conforme Description do template. */
export const CHECKLIST_ACCESSORY_GROUPS: ChecklistAccessoryGroup[] = [
  {
    title: 'Vistoria - Carenagem / Plásticos',
    items: [
      { key: 'acc_19', templateId: 19, label: 'Chapeuzinho' },
      { key: 'acc_20', templateId: 20, label: 'Bananinha Lado Direito' },
      { key: 'acc_21', templateId: 21, label: 'Bananinha Lado Esquerdo' },
      { key: 'acc_22', templateId: 22, label: 'Paralama Dianteiro' },
      { key: 'acc_23', templateId: 23, label: 'Paralama Traseiro' },
      { key: 'acc_24', templateId: 24, label: 'Aba do Tanque Esquerda' },
      { key: 'acc_25', templateId: 25, label: 'Aba do Tanque Direita' },
      { key: 'acc_26', templateId: 26, label: 'Lateral Direita' },
      { key: 'acc_27', templateId: 27, label: 'Lateral Esquerda' },
      { key: 'acc_28', templateId: 28, label: 'Rabeta Traseira Esquerda' },
      { key: 'acc_29', templateId: 29, label: 'Rabeta Traseira Direita' },
      { key: 'acc_30', templateId: 30, label: 'Rabeta Traseira (Tubarão)' },
      { key: 'acc_31', templateId: 31, label: 'Rabeta Anti-Grau' },
      { key: 'acc_32', templateId: 32, label: 'Tampa do Filtro de Ar' }
    ]
  },
  {
    title: 'Vistoria - Iluminação',
    items: [
      { key: 'acc_33', templateId: 33, label: 'Bloco Óptico' },
      { key: 'acc_34', templateId: 34, label: 'Pisca Dianteiro Esquerdo / Traseiro Direito' },
      { key: 'acc_35', templateId: 35, label: 'Pisca Dianteiro Direito / Traseiro Esquerdo' },
      { key: 'acc_36', templateId: 36, label: 'Lanterna Traseira' },
      { key: 'acc_37', templateId: 37, label: 'Aranha do Farol' },
      { key: 'acc_38', templateId: 38, label: 'Olho de Gato' }
    ]
  },
  {
    title: 'Vistoria - Acabamentos / Externos',
    items: [
      { key: 'acc_39', templateId: 39, label: 'Retrovisor' },
      { key: 'acc_40', templateId: 40, label: 'Manopla' },
      { key: 'acc_41', templateId: 41, label: 'Alça do Garupa' },
      { key: 'acc_42', templateId: 42, label: 'Suporte de Celular' },
      { key: 'acc_43', templateId: 43, label: 'Placa' }
    ]
  },
  {
    title: 'Vistoria - Rodas / Suspensão / Estrutura',
    items: [
      { key: 'acc_44', templateId: 44, label: 'Roda Dianteira' },
      { key: 'acc_45', templateId: 45, label: 'Roda Traseira' },
      { key: 'acc_46', templateId: 46, label: 'Estribo Central' },
      { key: 'acc_47', templateId: 47, label: 'Borracha do Estribo' },
      { key: 'acc_48', templateId: 48, label: 'Pedaleira Traseira' }
    ]
  },
  {
    title: 'Vistoria - Motor / Escape / Transmissão',
    items: [
      { key: 'acc_49', templateId: 49, label: 'Escapamento' },
      { key: 'acc_50', templateId: 50, label: 'Protetor de Escapamento' },
      { key: 'acc_51', templateId: 51, label: 'Protetor de Corrente' },
      { key: 'acc_52', templateId: 52, label: 'Pedal de Câmbio' },
      { key: 'acc_53', templateId: 53, label: 'Pedal de Freio' },
      { key: 'acc_54', templateId: 54, label: 'Filtro de Ar' }
    ]
  },
  {
    title: 'Vistoria - Elétrica / Ignição',
    items: [
      { key: 'acc_55', templateId: 55, label: 'Painel' },
      { key: 'acc_56', templateId: 56, label: 'Bateria' },
      { key: 'acc_57', templateId: 57, label: 'Kit Ignição' },
      { key: 'acc_58', templateId: 58, label: 'Carregador' },
      { key: 'acc_59', templateId: 59, label: 'Cópia de Chave' }
    ]
  },
  {
    title: 'Vistoria - Banco',
    items: [
      { key: 'acc_60', templateId: 60, label: 'Banco' },
      { key: 'acc_61', templateId: 61, label: 'Capa de Banco' }
    ]
  },
  {
    title: 'Vistoria - Comandos',
    items: [
      { key: 'acc_62', templateId: 62, label: 'Guidão' },
      { key: 'acc_63', templateId: 63, label: 'Manete de Freio' },
      { key: 'acc_64', templateId: 64, label: 'Manete de Embreagem' }
    ]
  }
];

export const CHECKLIST_ACCESSORY_TEMPLATE_IDS = CHECKLIST_ACCESSORY_GROUPS.flatMap((g) =>
  g.items.map((i) => i.templateId)
);

export function accessoryFormKey(templateId: number): string {
  return `acc_${templateId}`;
}

export function mapSnaToCondition(value: string | null | undefined): string {
  if (!value) return 'Sim';
  const v = value.toUpperCase();
  if (v === 'S' || v === 'SIM') return 'Sim';
  if (v === 'N' || v === 'NÃO' || v === 'NAO') return 'Não';
  if (v === 'A' || v === 'AVARIADO') return 'Avariado';
  return value;
}

export function mapConditionToSna(condition: string | null | undefined): string {
  if (!condition) return '';
  const c = condition.toLowerCase();
  if (c === 'sim') return 'S';
  if (c === 'não' || c === 'nao') return 'N';
  if (c === 'avariado') return 'A';
  return '';
}

export function buildAccessoryFormControls(): Record<string, string[]> {
  const controls: Record<string, string[]> = {};
  for (const group of CHECKLIST_ACCESSORY_GROUPS) {
    for (const item of group.items) {
      controls[item.key] = [''];
    }
  }
  return controls;
}

export function getAccessoryGroupTitleByTemplateId(templateId: number): string {
  for (const group of CHECKLIST_ACCESSORY_GROUPS) {
    if (group.items.some((i) => i.templateId === templateId)) {
      return group.title;
    }
  }
  return 'Outros';
}

export function buildTemplateIdToFieldName(): Record<number, string> {
  const map: Record<number, string> = {
    [CHECKLIST_TEMPLATE_IDS.plate]: 'Placa do Veículo',
    [CHECKLIST_TEMPLATE_IDS.model]: 'Modelo do Veículo',
    [CHECKLIST_TEMPLATE_IDS.odometer]: 'Quilometragem',
    [CHECKLIST_TEMPLATE_IDS.checklistDate]: 'Data do Checklist',
    [CHECKLIST_TEMPLATE_IDS.checklistTime]: 'Hora do Checklist',
    [CHECKLIST_TEMPLATE_IDS.driverName]: 'Nome do Condutor',
    [CHECKLIST_TEMPLATE_IDS.driverCpf]: 'CPF do Condutor',
    [CHECKLIST_TEMPLATE_IDS.driverPhone]: 'Telefone do Condutor',
    [CHECKLIST_TEMPLATE_IDS.panelKm]: 'KM do Painel',
    [CHECKLIST_TEMPLATE_IDS.tireCondition]: 'Condição do Pneu',
    [CHECKLIST_TEMPLATE_IDS.fuelLevel]: 'Nível de Combustível',
    [CHECKLIST_TEMPLATE_IDS.isDirty]: 'Veículo Está Sujo',
    [CHECKLIST_TEMPLATE_IDS.photoFront]: 'Foto Frontal',
    [CHECKLIST_TEMPLATE_IDS.photoRight]: 'Foto Lado Direito',
    [CHECKLIST_TEMPLATE_IDS.photoLeft]: 'Foto Lado Esquerdo',
    [CHECKLIST_TEMPLATE_IDS.photoTop]: 'Foto Superior',
    [CHECKLIST_TEMPLATE_IDS.photoBack]: 'Foto Traseira',
    [CHECKLIST_TEMPLATE_IDS.detailPhoto]: 'Foto Detalhada',
    [CHECKLIST_TEMPLATE_IDS.observations]: 'Observações Gerais',
    [CHECKLIST_TEMPLATE_IDS.signatureName]: 'Nome do Condutor',
    [CHECKLIST_TEMPLATE_IDS.signaturePhone]: 'Telefone do Condutor',
    [CHECKLIST_TEMPLATE_IDS.signatureEmail]: 'Email do Condutor',
    [CHECKLIST_TEMPLATE_IDS.signatureCpf]: 'CPF do Condutor',
    [CHECKLIST_TEMPLATE_IDS.signaturePhoto]: 'Assinatura do Condutor',
    [CHECKLIST_TEMPLATE_IDS.documentPdf]: 'Documento Pdf'
  };
  for (const group of CHECKLIST_ACCESSORY_GROUPS) {
    for (const item of group.items) {
      map[item.templateId] = item.label;
    }
  }
  return map;
}
